import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Droplets, Sun, Thermometer } from 'lucide-react';
import { featuredPlants } from '../data/plantsData';

const FeaturedPlants = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="plants" className="py-20 bg-gray-50 text-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl font-bold mb-4 text-gray-900">
            Featured Plant Collection
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover our carefully curated selection of plants perfect for beginners and experts alike. 
            Each plant comes with detailed care instructions to ensure your green friends thrive.
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {featuredPlants.map((plant) => (
            <motion.div
              key={plant.id}
              className="bg-white rounded-xl overflow-hidden border border-gray-200 group transition-all duration-300 hover:border-primary-500 hover:shadow-lg"
              variants={cardVariants}
              whileHover={{ y: -8 }}
            >
              <div className="relative overflow-hidden">
                <img
                  src={plant.image}
                  alt={plant.name}
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>

              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {plant.name}
                </h3>
                <p className="text-gray-600 mb-4 leading-relaxed h-24 overflow-hidden">
                  {plant.description}
                </p>

                {/* Care info */}
                <div className="space-y-3 mb-6 border-t border-gray-200 pt-4">
                  <div className="flex items-center text-sm text-gray-700">
                    <Sun className="h-5 w-5 mr-3 text-yellow-500" />
                    <span className="font-semibold w-20">Light:</span>
                    <span className="ml-1">{plant.care.light}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <Droplets className="h-5 w-5 mr-3 text-blue-500" />
                    <span className="font-semibold w-20">Water:</span>
                    <span className="ml-1">{plant.care.water}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <Thermometer className="h-5 w-5 mr-3 text-red-500" />
                    <span className="font-semibold w-20">Temp:</span>
                    <span className="ml-1">{plant.care.temperature}</span>
                  </div>
                </div>

                <motion.button
                  className="group/btn w-full bg-gradient-to-r from-primary-500 to-green-500 hover:from-primary-600 hover:to-green-600 text-white py-3 px-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center space-x-2 transform hover:scale-105 shadow-md hover:shadow-lg"
                  whileTap={{ scale: 0.98 }}
                >
                  <span>Learn More</span>
                  <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform duration-300" />
                </motion.button>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <motion.button
            className="group bg-primary-600 hover:bg-primary-700 text-white border border-primary-600 hover:border-primary-700 px-8 py-3 rounded-full font-semibold transition-all duration-300 flex items-center space-x-2 transform hover:scale-105 shadow-md hover:shadow-lg"
            whileTap={{ scale: 0.95 }}
          >
            <span>View All Plants</span>
            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturedPlants;
